import { Component, ElementRef, OnInit } from "@angular/core";
import { filter, merge, of, Subscription, takeUntil, take } from "rxjs";
import { DestroyableMixin } from "apps-module/app/shared-library/mixins/destroyable.mixin";
import { LockMixin } from "apps-module/app/shared-library/mixins/lock.mixin";
import { ConfigurationService } from "apps-module/app/shared-library/services/configuration/configuration.service";
import { BrowserWindowService } from "apps-module/app/shared-library/services/environment/browser-window.service";
import { EmptyInstance } from "core-library/mixins/empty-instance";
import { SearchConfigurationModel } from "apps-module/app/entry-modules/charity-search/models/search-configuration.model";
import { FormSearchParametersService } from "apps-module/app/entry-modules/charity-search/services/form-search-parameters.service";
import { SearchService } from "apps-module/app/entry-modules/charity-search/services/search.service";
import { QuerySearchParametersService } from "apps-module/app/entry-modules/charity-search/services/query-search-parameters.service";
import { SearchForm } from "apps-module/app/entry-modules/charity-search/forms/search.form";
import { FormSearchParametersModel } from "apps-module/app/entry-modules/charity-search/models/form-search-parameters.model";
import { SearchResultItemModel } from "apps-module/app/entry-modules/charity-search/models/search-result-item.model";
import { Pending } from "apps-module/app/shared-library/decorators/pending.decorator";
import { SearchStatus } from "apps-module/app/shared-library/enums/search-status.enum";
import { SearchResultModel } from "apps-module/app/entry-modules/charity-search/models/search-result.model";
import { SearchFilterOptionModel } from "apps-module/app/entry-modules/charity-search/models/search-filter-option.model";

@Component({
    selector: CharitySearchComponent.SELECTOR,
    templateUrl: "./charity-search.component.html",
    providers: [ConfigurationService, FormSearchParametersService, SearchService, QuerySearchParametersService]
})
export class CharitySearchComponent extends DestroyableMixin(LockMixin(EmptyInstance)) implements OnInit {

    public static SELECTOR: string = "[pcg-ng-charity-search]";

    // TODO: is this decorator necessary?
    @Pending
    private _onSearch: Subscription;

    public configuration: SearchConfigurationModel;
    public searchForm: SearchForm;
    public results: Array<SearchResultItemModel> = [];
    public aooa: Array<SearchFilterOptionModel> = [];
    public aoob: Array<SearchFilterOptionModel> = [];
    public aood: Array<SearchFilterOptionModel> = [];
    public classification1: Array<SearchFilterOptionModel> = [];
    public classification2: Array<SearchFilterOptionModel> = [];
    public classification3: Array<SearchFilterOptionModel> = [];
    public income: Array<SearchFilterOptionModel> = [];
    public site: Array<SearchFilterOptionModel> = [];
    public include: Array<SearchFilterOptionModel> = [];
    public orgType: Array<SearchFilterOptionModel> = [];
    public totalPages: number = 1;
    public totalCount: number = 0;
    public searchStatus: SearchStatus = SearchStatus.Loading;
    public searchText: string = null;
    public readonly statuses = SearchStatus;
    public isOpen: { [key: string]: boolean } = {
        aooa: false,
        aoob: false,
        aood: false,
        classification1: false,
        classification2: false,
        classification3: false,
        income: false,
        site: false,
        include: false,
        orgType: false
    };
    public exportToCsvUrl: string;

    public constructor(
        private _domElement: ElementRef,
        private _browserWindowService: BrowserWindowService,
        private _configurationService: ConfigurationService<SearchConfigurationModel>,
        private _searchParametersFactory: FormSearchParametersService,
        private _searchService: SearchService,
        private _queryStringSetterService: QuerySearchParametersService
    ) { super(); }

    public ngOnInit(): void {
        this._configurationService.initialize(this._domElement);

        this.configuration = this._configurationService.getConfiguration();

        const queryString = this._browserWindowService.getQueryString();
        const searchParameters = this._searchParametersFactory.create(queryString, this.configuration);
        this.searchForm = SearchForm.createForm(searchParameters);

        merge(of(searchParameters), this.searchForm.onFormSubmitted)
            .pipe(
                filter(() => !this.isLocked),
                takeUntil(this.onDestroy)
            )
            .subscribe(searchParams => this.invokeSearch(searchParams));
    }

    public toggleFilter(filterName: string): void {
        this.isOpen[filterName] = !this.isOpen[filterName];
    }

    public checkFiltersIfAnySelected(): boolean {
        return this.aooa.some(item => item.selected) ||
            this.aoob.some(items => items.selected) ||
            this.aood.some(item => item.selected) ||
            this.classification1.some(item => item.selected) ||
            this.classification2.some(items => items.selected) ||
            this.classification3.some(item => item.selected)||
            this.income.some(item => item.selected) ||
            this.site.some(items => items.selected) ||
            this.include.some(item => item.selected)||
            this.orgType.some(item => item.selected);
    }

    public clearFilters(): void {
        this.searchForm.aooa.setValue([]);
        this.searchForm.aoob.setValue([]);
        this.searchForm.aood.setValue([]);
        this.searchForm.classification1.setValue([]);
        this.searchForm.classification2.setValue([]);
        this.searchForm.classification3.setValue([]);
        this.searchForm.income.setValue([]);
        this.searchForm.site.setValue([]);
        this.searchForm.include.setValue([]);
        this.searchForm.orgType.setValue([]);

        this.searchForm.searchByFilters();
    }

    private invokeSearch(formSearchParameters: FormSearchParametersModel): void {
        this.searchText = formSearchParameters.searchText;
        this.searchStatus = SearchStatus.Loading;
        this._queryStringSetterService.set(formSearchParameters);

        this._onSearch = this._searchService.search(formSearchParameters)
            .pipe(take(1))
            .subscribe({
                next: (response: SearchResultModel) => {
                    this.aooa = [...response.aooa];
                    this.aoob = [...response.aoob];
                    this.aood = [...response.aood];
                    this.classification1 = [...response.classification1];
                    this.classification2 = [...response.classification2];
                    this.classification3 = [...response.classification3];
                    this.income = [...response.income];
                    this.site = [...response.site];
                    this.include = [...response.include];
                    this.orgType = [...response.orgType];

                    const searchResult = {...response.results};
                    this.totalCount = searchResult.totalItems ?? 0;
                    this.totalPages = searchResult.totalPages ?? 1;
                    this.results = [...searchResult.pageItems];
                    this.searchStatus = SearchStatus.Success;

                    this.populateSearchResults(response);
                },
                error: () => this.searchStatus = SearchStatus.Error
            });
    }

    private populateSearchResults(searchResults: SearchResultModel): void {
        const queryString = this._browserWindowService.getQueryString();
        const searchParams = this._searchParametersFactory.create(queryString, this.configuration);

        const queryParams = new URLSearchParams();
        for (const key in searchParams) {
            const value = searchParams[key as keyof FormSearchParametersModel];

            if (Array.isArray(value)) {
                value.forEach(item => {
                    queryParams.append(key, item);
                });
            } else if (value !== undefined && value !== null) {
                queryParams.set(key, value.toString());
            }
        }

        if (searchResults.results.pageItems) {
            this.exportToCsvUrl =
                `/api/charity-search/exportSearchResultsToCsv/?${queryParams.toString()}`;
        }
    }

    public getTabUrl(tab: { url: string }): string {
        const searchText = this.searchForm.searchText?.value?.trim();
        return searchText
            ? `${tab.url}?searchText=${encodeURIComponent(searchText)}`
            : tab.url;
    }
}
